import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { isGlobalSuperAdmin } from "@/lib/permissions";
import { LoadingOverlay } from "@/components/ui/loading-spinner";
import HomePage from "@/pages/HomePage";
import TenantLandingPage from "@/pages/TenantLandingPage";
import TenantNotFoundPage from "@/pages/TenantNotFoundPage";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import OnboardingPage from "@/pages/OnboardingPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StaffAcceptInvitePage from "@/pages/StaffAcceptInvitePage";
import { StaffProfilePage } from "@/pages/staff/StaffProfilePage";
import DomainSettingsPage from "@/pages/admin/DomainSettingsPage";
import SettingsOverviewPage from "@/pages/admin/SettingsOverviewPage";
import BrandingSettingsPage from "@/pages/admin/settings/BrandingSettingsPage";
import EmailTemplatesSettingsPage from "@/pages/admin/settings/EmailTemplatesSettingsPage";
import SmsTemplatesSettingsPage from "@/pages/admin/settings/SmsTemplatesSettingsPage";
import WelcomeMessageSettingsPage from "@/pages/admin/settings/WelcomeMessageSettingsPage";
import GeneralPoliciesSettingsPage from "@/pages/admin/settings/GeneralPoliciesSettingsPage";
import PatronTypesSettingsPage from "@/pages/admin/settings/PatronTypesSettingsPage";
import PatronTypeFormPage from "@/pages/admin/settings/PatronTypeFormPage";
import ItemTypesSettingsPage from "@/pages/admin/settings/ItemTypesSettingsPage";
import ItemTypeFormPage from "@/pages/admin/settings/ItemTypeFormPage";
import LocationsSettingsPage from "@/pages/admin/settings/LocationsSettingsPage";
import LocationFormPage from "@/pages/admin/settings/LocationFormPage";
import CollectionsSettingsPage from "@/pages/admin/settings/CollectionsSettingsPage";
import CollectionFormPage from "@/pages/admin/settings/CollectionFormPage";
import ItemStatusesSettingsPage from "@/pages/admin/settings/ItemStatusesSettingsPage";
import BookConditionsSettingsPage from "@/pages/admin/settings/BookConditionsSettingsPage";
import BookConditionFormPage from "@/pages/admin/settings/BookConditionFormPage";
import StaffManagementPage from "@/pages/admin/settings/StaffManagementPage";
import StaffFormPage from "@/pages/admin/settings/StaffFormPage";
import StaffDetailsPage from "@/pages/admin/settings/StaffDetailsPage";
import DepartmentsSettingsPage from "@/pages/admin/settings/DepartmentsSettingsPage";
import DepartmentFormPage from "@/pages/admin/settings/DepartmentFormPage";
import JobTitlesSettingsPage from "@/pages/admin/settings/JobTitlesSettingsPage";
import JobTitleFormPage from "@/pages/admin/settings/JobTitleFormPage";
import BranchesSettingsPage from "@/pages/admin/settings/BranchesSettingsPage";
import BranchFormPage from "@/pages/admin/settings/BranchFormPage";
import BranchPermissionsPage from "@/pages/admin/settings/BranchPermissionsPage";
import BranchOverviewPage from "@/pages/admin/branches/BranchOverviewPage";
import BranchStaffPage from "@/pages/admin/branches/BranchStaffPage";
import BranchHoursPage from "@/pages/admin/branches/BranchHoursPage";
import BranchSettingsPage from "@/pages/admin/branches/BranchSettingsPage";
import BranchInventoryPage from "@/pages/admin/branches/BranchInventoryPage";
import BranchTransfersPage from "@/pages/admin/branches/BranchTransfersPage";
import AllTransfersPage from "@/pages/admin/branches/AllTransfersPage";
import RolesSettingsPage from "@/pages/admin/settings/RolesSettingsPage";
import RoleFormPage from "@/pages/admin/settings/RoleFormPage";
import StaffCustomFieldsSettingsPage from "@/pages/admin/settings/StaffCustomFieldsSettingsPage";
import IntegrationsSettingsPage from "@/pages/admin/settings/IntegrationsSettingsPage";
import RegistrationSetupPage from "@/pages/admin/setup/RegistrationSetupPage";
import BarcodePatternSetupPage from "@/pages/admin/setup/BarcodePatternSetupPage";
import AccessionNumberSetupPage from "@/pages/admin/setup/AccessionNumberSetupPage";
import CatalogApprovalSetupPage from "@/pages/admin/setup/CatalogApprovalSetupPage";
import SecurityAuditPage from "@/pages/admin/setup/SecurityAuditPage";
import RegisterPage from "@/pages/RegisterPage";
import CardProcessingPage from "@/pages/admin/CardProcessingPage";
import LibraryCardPrintPage from "@/pages/admin/LibraryCardPrintPage";
import VerifyCardPage from "@/pages/VerifyCardPage";
import VerifyReceiptPage from "@/pages/VerifyReceiptPage";
import RegistrationApprovalPage from "@/pages/admin/RegistrationApprovalPage";
import { TenantManagementPage } from "@/pages/admin/TenantManagementPage";
import { BillingPlansPage } from "@/pages/admin/BillingPlansPage";
import { TenantBillingPage } from "@/pages/billing/TenantBillingPage";
import { IsbnCatalogPage } from "@/pages/admin/catalog/IsbnCatalogPage";
import { BulkImportPage } from "@/pages/admin/catalog/BulkImportPage";
import { BulkExportPage } from "@/pages/admin/catalog/BulkExportPage";
import { CatalogManagementPage } from "@/pages/admin/catalog/CatalogManagementPage";
import { UploadEResourcePage } from "@/pages/admin/catalog/UploadEResourcePage";
import { EditDigitalResourcePage } from "@/pages/admin/catalog/EditDigitalResourcePage";
import { ManualCatalogPage } from "@/pages/admin/catalog/ManualCatalogPage";
import { AcquisitionCatalogingWizardPage } from "@/pages/admin/catalog/AcquisitionCatalogingWizardPage";
import { JournalCatalogPage } from "@/pages/admin/catalog/JournalCatalogPage";
import { JournalDetailsPage } from "@/pages/admin/catalog/JournalDetailsPage";
import { BookDetailsPage } from "@/pages/admin/catalog/BookDetailsPage";
import { DigitalResourceDetailsPage } from "@/pages/admin/catalog/DigitalResourceDetailsPage";
import { DigitalResourceViewPage } from "@/pages/public/DigitalResourceViewPage";
import { GuestCatalogPage } from "@/pages/public/GuestCatalogPage";
import { GuestContactPage } from "@/pages/public/GuestContactPage";
import { GuestResourceDetailPage } from "@/pages/public/GuestResourceDetailPage";
import { InvoicesPage } from "@/pages/dashboard/InvoicesPage";
import { InvoiceReceiptPage } from "@/pages/dashboard/InvoiceReceiptPage";
import { ManageInvoicesPage } from "@/pages/admin/ManageInvoicesPage";
import { AdminInvoiceReceiptPage } from "@/pages/admin/AdminInvoiceReceiptPage";
import PaymentCallbackPage from "@/pages/PaymentCallbackPage";
import { AppLayout } from "@/components/layout/AppLayout";
import DesignSystemPage from "@/pages/DesignSystemPage";
import ContactPage from "@/pages/ContactPage";
import CirculationDeskPage from "@/pages/circulation/CirculationDeskPage";
import HoldsPage from "@/pages/circulation/HoldsPage";
import OverduePage from "@/pages/circulation/OverduePage";
import HistoryPage from "@/pages/circulation/HistoryPage";
import FinesManagementPage from "@/pages/circulation/FinesManagementPage";
import PatronsListPage from "@/pages/admin/PatronsListPage";
import PatronDetailPage from "@/pages/admin/PatronDetailPage";
import MyBooksPage from "@/pages/patron/MyBooksPage";
import MyHoldsPage from "@/pages/patron/MyHoldsPage";
import MyFinesPage from "@/pages/patron/MyFinesPage";
import PaidResourcesPage from "@/pages/patron/PaidResourcesPage";
import PaidResourceDetailPage from "@/pages/patron/PaidResourceDetailPage";
import PaidResourcePurchasesPage from "@/pages/admin/catalog/PaidResourcePurchasesPage";
import PaidResourceEntitlementsPage from "@/pages/admin/catalog/PaidResourceEntitlementsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import DocumentationPage from "@/pages/documentation/DocumentationPage";
import UserServicesGuidePage from "@/pages/internal/UserServicesGuidePage";
import MyRecommendationsPage from "@/pages/patron/MyRecommendationsPage";
import MyRecommendationDetailPage from "@/pages/patron/MyRecommendationDetailPage";
import CirculationPoliciesPage from "@/pages/admin/settings/CirculationPoliciesPage";
import VendorsPage from "@/pages/acquisitions/VendorsPage";
import VendorFormPage from "@/pages/acquisitions/VendorFormPage";
import VendorDetailPage from "@/pages/acquisitions/VendorDetailPage";
import BudgetOverviewPage from "@/pages/acquisitions/BudgetOverviewPage";
import FundFormPage from "@/pages/acquisitions/FundFormPage";
import FundDetailPage from "@/pages/acquisitions/FundDetailPage";
import AcquisitionRequestsPage from "@/pages/acquisitions/AcquisitionRequestsPage";
import NewAcquisitionRequestPage from "@/pages/acquisitions/NewAcquisitionRequestPage";
import AcquisitionRequestDetailPage from "@/pages/acquisitions/AcquisitionRequestDetailPage";
import PurchaseOrdersPage from "@/pages/acquisitions/PurchaseOrdersPage";
import NewPurchaseOrderPage from "@/pages/acquisitions/NewPurchaseOrderPage";
import PurchaseOrderDetailPage from "@/pages/acquisitions/PurchaseOrderDetailPage";
import ReceivingDashboardPage from "@/pages/acquisitions/ReceivingDashboardPage";
import AcqInvoicesPage from "@/pages/acquisitions/AcqInvoicesPage";
import NewAcqInvoicePage from "@/pages/acquisitions/NewAcqInvoicePage";
import AcqInvoiceDetailPage from "@/pages/acquisitions/AcqInvoiceDetailPage";
import PatronRecommendPage from "@/pages/acquisitions/PatronRecommendPage";
import AcquisitionsReportsPage from "@/pages/acquisitions/AcquisitionsReportsPage";
import CatalogingProductivityReportPage from "@/pages/admin/reports/CatalogingProductivityReportPage";
import MyCatalogStatsPage from "@/pages/staff/catalog/MyCatalogStatsPage";
import CatalogDraftsPage from "@/pages/staff/catalog/CatalogDraftsPage";
import CatalogReviewQueuePage from "@/pages/staff/catalog/CatalogReviewQueuePage";
import BinderyDashboardPage from "@/pages/bindery/BinderyDashboardPage";
import BinderyVendorsPage from "@/pages/bindery/BinderyVendorsPage";
import BinderyVendorFormPage from "@/pages/bindery/BinderyVendorFormPage";
import BinderyVendorDetailPage from "@/pages/bindery/BinderyVendorDetailPage";
import BinderyRequestsPage from "@/pages/bindery/BinderyRequestsPage";
import NewBinderyRequestPage from "@/pages/bindery/NewBinderyRequestPage";
import BinderyRequestDetailPage from "@/pages/bindery/BinderyRequestDetailPage";
import BinderyHistoryPage from "@/pages/bindery/BinderyHistoryPage";
import ThesisRequestsPage from "@/pages/bindery/thesis/ThesisRequestsPage";
import ThesisRequestDetailPage from "@/pages/bindery/thesis/ThesisRequestDetailPage";
import ThesisPricingSettingsPage from "@/pages/bindery/thesis/ThesisPricingSettingsPage";
import MyThesisRequestsPage from "@/pages/patron/thesis/MyThesisRequestsPage";
import NewThesisRequestPage from "@/pages/patron/thesis/NewThesisRequestPage";
import EditThesisRequestPage from "@/pages/patron/thesis/EditThesisRequestPage";
import MyThesisDetailPage from "@/pages/patron/thesis/MyThesisDetailPage";
import MyTicketsPage from "@/pages/support/MyTicketsPage";
import NewTicketPage from "@/pages/support/NewTicketPage";
import SupportInboxPage from "@/pages/support/SupportInboxPage";
import TicketDetailPage from "@/pages/support/TicketDetailPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Wrapper component to provide AppLayout for admin pages
const DashboardPageWrapper = ({ children }: { children: React.ReactNode }) => {
  return <AppLayout>{children}</AppLayout>;
};

// Redirect old /dashboard/invoices/:id/receipt to flat path
const ReceiptRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={id ? `/invoices/${id}/receipt` : "/invoices"} replace />;
};

const AppRoutes = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { tenantNotFound, isMainPlatform } = useTenant();

  const userPermissions = user?.permissions || [];
  const isSuperAdmin = isGlobalSuperAdmin(user);

  // Check if user is Tenant Admin (works for both regular users and staff).
  // Uses exact match after normalization to prevent "Library Admin" from matching.
  const isTenantAdmin = Boolean(
    isSuperAdmin ||
      (user?.isStaff
        ? (() => {
            const normalized = user?.roleName?.toUpperCase()?.replace(/\s+/g, "_") || "";
            return normalized === "TENANT_ADMIN";
          })()
        : user?.memberships?.some(
            (m) => m.roleName === "Tenant Admin" || m.role === "TENANT_ADMIN"
          ))
  );

  // Check if user is Librarian (works for both regular users and staff)
  const isLibrarian = Boolean(
    user?.isStaff
      ? user?.roleName?.toUpperCase() === "LIBRARIAN"
      : user?.memberships?.some((m) => m.roleName === "Librarian")
  );

  // Helper: check if user has a specific permission
  const hasPermission = (perm: string): boolean => {
    if (isSuperAdmin) return true;
    return userPermissions.some(
      (p: string) =>
        p === perm || p === "*" || (p.endsWith(".*") && perm.startsWith(p.slice(0, -2) + "."))
    );
  };

  // Check if user has card processing permission

  const _hasCardPermission = Boolean(
    user?.permissions?.some((p: string) => p === "cards.process" || p === "cards.*" || p === "*")
  );

  // If tenant not found and not on main platform, show 404 page
  if (tenantNotFound && !isMainPlatform) {
    return (
      <Routes>
        <Route path="*" element={<TenantNotFoundPage />} />
      </Routes>
    );
  }

  // Show loading state while checking authentication
  const LoadingScreen = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LoadingOverlay size="xl" label="Loading your session..." />
    </div>
  );

  // Public route wrapper (no authentication required)
  const PublicRoute = ({ children }: { children: React.ReactNode }) => {
    return <>{children}</>;
  };

  // Protected route wrapper that handles loading and authentication
  const ProtectedRoute = ({
    children,
    requireTenantAdmin = false,
    requireLibrarian = false,
    requireSuperAdmin = false,
    requireTenantAdminOrLibrarian = false,
    requiredPermissions,
    customRoleCheck,
  }: {
    children: React.ReactNode;
    requireTenantAdmin?: boolean;
    requireLibrarian?: boolean;
    requireSuperAdmin?: boolean;
    requireTenantAdminOrLibrarian?: boolean;
    /** Require at least one of these permissions (Tenant Admin / Super Admin bypass) */
    requiredPermissions?: string[];
    customRoleCheck?: () => boolean;
  }) => {
    // Show loading while checking auth
    if (isLoading) {
      return <LoadingScreen />;
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      const fullReturnUrl = window.location.pathname + window.location.search;
      const publicRoutes = [
        "/login",
        "/",
        "/onboard",
        "/register",
        "/verify-email",
        "/verify-card",
        "/verify-receipt",
        "/reset-password",
        "/staff/accept-invite",
      ];
      if (!publicRoutes.includes(window.location.pathname)) {
        sessionStorage.setItem("lastLocation", fullReturnUrl);
        return <Navigate to={`/login?returnUrl=${encodeURIComponent(fullReturnUrl)}`} replace />;
      }
      return <Navigate to="/login" replace />;
    }

    // Check custom role check first if provided
    if (customRoleCheck && !customRoleCheck()) {
      return <Navigate to="/dashboard" replace />;
    }

    // Check role requirements.
    // Staff access is permission-based: catalog routes need catalog.view, etc.
    if (requireTenantAdmin && !isTenantAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
    if (requireLibrarian && !(isLibrarian || isTenantAdmin || hasPermission("catalog.view"))) {
      return <Navigate to="/dashboard" replace />;
    }
    if (requireSuperAdmin && !isSuperAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
    if (
      requireTenantAdminOrLibrarian &&
      !(isTenantAdmin || isLibrarian || hasPermission("catalog.view"))
    ) {
      return <Navigate to="/dashboard" replace />;
    }
    if (
      requiredPermissions &&
      requiredPermissions.length > 0 &&
      !(isTenantAdmin || isSuperAdmin || requiredPermissions.some((p) => hasPermission(p)))
    ) {
      return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
  };

  return (
    <Routes>
      <Route path="/" element={isMainPlatform ? <HomePage /> : <TenantLandingPage />} />
      <Route
        path="/designsystem"
        element={isMainPlatform ? <DesignSystemPage /> : <Navigate to="/login" replace />}
      />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/onboard"
        element={isMainPlatform ? <OnboardingPage /> : <Navigate to="/login" replace />}
      />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/verify-card" element={<VerifyCardPage />} />
      <Route path="/verify-receipt" element={<VerifyReceiptPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/staff/accept-invite" element={<StaffAcceptInvitePage />} />
      {/* Public Digital Resource Viewer - no auth required */}
      <Route path="/view/:id" element={<DigitalResourceViewPage />} />
      {/* Guest OPAC — tenant subdomain only; main platform redirects home */}
      <Route
        path="/opac"
        element={isMainPlatform ? <Navigate to="/" replace /> : <GuestCatalogPage />}
      />
      <Route
        path="/opac/view/:publicId"
        element={isMainPlatform ? <Navigate to="/" replace /> : <GuestResourceDetailPage />}
      />
      <Route
        path="/opac/contact"
        element={isMainPlatform ? <Navigate to="/" replace /> : <GuestContactPage />}
      />
      <Route
        path="/dashboard"
        element={
          isLoading ? (
            <LoadingScreen />
          ) : isAuthenticated ? (
            <DashboardPage />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      {/* Redirect old /admin/dashboard route to unified /dashboard */}
      <Route path="/admin/dashboard" element={<Navigate to="/dashboard" replace />} />
      {/* Redirect old /dashboard/* routes to flat equivalents */}
      <Route path="/dashboard/invoices" element={<Navigate to="/invoices" replace />} />
      <Route path="/dashboard/invoices/:id/receipt" element={<ReceiptRedirect />} />
      <Route path="/dashboard/profile" element={<Navigate to="/profile" replace />} />
      <Route path="/dashboard/tenants" element={<Navigate to="/tenants" replace />} />
      <Route path="/dashboard/billing-plans" element={<Navigate to="/billing-plans" replace />} />
      {/* Payment callback (after Paystack redirect) — public so verification does not depend on session order */}
      <Route path="/payment/callback" element={<PaymentCallbackPage />} />
      {/* Patron Invoices */}
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <InvoicesPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id/receipt"
        element={
          <ProtectedRoute>
            <InvoiceReceiptPage />
          </ProtectedRoute>
        }
      />
      {/* Staff Profile Page */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <StaffProfilePage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Setup Overview Page */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute requireTenantAdmin>
            <SettingsOverviewPage />
          </ProtectedRoute>
        }
      />
      {/* Individual Setup Pages */}
      <Route
        path="/setup/domain"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DomainSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branding"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BrandingSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/email-templates"
        element={
          <ProtectedRoute requireTenantAdmin>
            <EmailTemplatesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/sms-templates"
        element={
          <ProtectedRoute requireTenantAdmin>
            <SmsTemplatesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/welcome-message"
        element={
          <ProtectedRoute requireTenantAdmin>
            <WelcomeMessageSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/general-policies"
        element={
          <ProtectedRoute requireTenantAdmin>
            <GeneralPoliciesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/circulation-policies"
        element={
          <ProtectedRoute requireTenantAdmin>
            <CirculationPoliciesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/patron-types"
        element={
          <ProtectedRoute requireTenantAdmin>
            <PatronTypesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/patron-types/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <PatronTypeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/patron-types/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <PatronTypeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/item-types"
        element={
          <ProtectedRoute requireTenantAdmin>
            <ItemTypesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/item-types/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <ItemTypeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/item-types/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <ItemTypeFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/locations"
        element={
          <ProtectedRoute requireTenantAdmin>
            <LocationsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/locations/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <LocationFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/locations/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <LocationFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/collections"
        element={
          <ProtectedRoute requireTenantAdmin>
            <CollectionsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/collections/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <CollectionFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/collections/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <CollectionFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/item-statuses"
        element={
          <ProtectedRoute requireTenantAdmin>
            <ItemStatusesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/book-conditions"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BookConditionsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/book-conditions/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BookConditionFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/book-conditions/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BookConditionFormPage />
          </ProtectedRoute>
        }
      />
      {/* Staff Management */}
      <Route
        path="/setup/staff"
        element={
          <ProtectedRoute requireTenantAdmin>
            <StaffManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/staff/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <StaffFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/staff/:id"
        element={
          <ProtectedRoute requireTenantAdmin>
            <StaffDetailsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/staff/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <StaffFormPage />
          </ProtectedRoute>
        }
      />
      {/* Departments */}
      <Route
        path="/setup/departments"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DepartmentsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/departments/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DepartmentFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/departments/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DepartmentFormPage />
          </ProtectedRoute>
        }
      />
      {/* Job Titles */}
      <Route
        path="/setup/job-titles"
        element={
          <ProtectedRoute requireTenantAdmin>
            <JobTitlesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/job-titles/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <JobTitleFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/job-titles/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <JobTitleFormPage />
          </ProtectedRoute>
        }
      />
      {/* Branches */}
      <Route
        path="/setup/branches"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/permissions"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchPermissionsPage />
          </ProtectedRoute>
        }
      />
      {/* Branch detail pages */}
      <Route
        path="/setup/branches/:id"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/staff"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchStaffPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/hours"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchHoursPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/settings"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/inventory"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchInventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/branches/:id/transfers"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BranchTransfersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/transfers"
        element={
          <ProtectedRoute requireTenantAdmin>
            <AllTransfersPage />
          </ProtectedRoute>
        }
      />
      {/* Roles */}
      <Route
        path="/setup/roles"
        element={
          <ProtectedRoute requireTenantAdmin>
            <RolesSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/roles/new"
        element={
          <ProtectedRoute requireTenantAdmin>
            <RoleFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/roles/:id/edit"
        element={
          <ProtectedRoute requireTenantAdmin>
            <RoleFormPage />
          </ProtectedRoute>
        }
      />
      {/* Staff Custom Fields */}
      <Route
        path="/setup/staff-custom-fields"
        element={
          <ProtectedRoute requireTenantAdmin>
            <StaffCustomFieldsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/registration"
        element={
          <ProtectedRoute requireTenantAdmin>
            <RegistrationSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/barcode-pattern"
        element={
          <ProtectedRoute requireTenantAdmin>
            <BarcodePatternSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/accession-number"
        element={
          <ProtectedRoute requireTenantAdmin>
            <AccessionNumberSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/catalog-approval"
        element={
          <ProtectedRoute requireTenantAdmin>
            <CatalogApprovalSetupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/integrations"
        element={
          <ProtectedRoute requireTenantAdmin>
            <IntegrationsSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/setup/security"
        element={
          <ProtectedRoute requireTenantAdmin requiredPermissions={["security.viewActivityLogs"]}>
            <SecurityAuditPage />
          </ProtectedRoute>
        }
      />
      {/* Card Processing */}
      <Route
        path="/admin/cards"
        element={
          <ProtectedRoute requireTenantAdminOrLibrarian>
            <DashboardPageWrapper>
              <CardProcessingPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cards/print"
        element={
          <ProtectedRoute requiredPermissions={["cards.print", "cards.process"]}>
            <LibraryCardPrintPage />
          </ProtectedRoute>
        }
      />
      {/* Patrons: detail must be registered before the list route */}
      <Route
        path="/patrons/:userId"
        element={
          <ProtectedRoute requiredPermissions={["patron.view"]}>
            <DashboardPageWrapper>
              <PatronDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/patrons"
        element={
          <ProtectedRoute requiredPermissions={["patron.list", "patron.view"]}>
            <DashboardPageWrapper>
              <PatronsListPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Registration Approval */}
      <Route
        path="/admin/registrations"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DashboardPageWrapper>
              <RegistrationApprovalPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Manage Invoices */}
      <Route
        path="/admin/invoices"
        element={
          <ProtectedRoute requiredPermissions={["invoices.view"]}>
            <DashboardPageWrapper>
              <ManageInvoicesPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/invoices/:id/receipt"
        element={
          <ProtectedRoute requiredPermissions={["invoices.view"]}>
            <AdminInvoiceReceiptPage />
          </ProtectedRoute>
        }
      />
      {/* ── Acquisitions ─────────────────────────────────── */}
      <Route
        path="/acquisitions/vendors"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <VendorsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/vendors/new"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.manageVendors"]}>
            <DashboardPageWrapper>
              <VendorFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/vendors/:id"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <VendorDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/vendors/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.manageVendors"]}>
            <DashboardPageWrapper>
              <VendorFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/budget"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <BudgetOverviewPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/budget/funds/new"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.manageBudget"]}>
            <DashboardPageWrapper>
              <FundFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/budget/funds/:id"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <FundDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/budget/funds/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.manageBudget"]}>
            <DashboardPageWrapper>
              <FundFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Acquisition Requests */}
      <Route
        path="/acquisitions/requests"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view", "acquisitions.createRequest"]}>
            <DashboardPageWrapper>
              <AcquisitionRequestsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/requests/new"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.createRequest"]}>
            <DashboardPageWrapper>
              <NewAcquisitionRequestPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/requests/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.createRequest"]}>
            <DashboardPageWrapper>
              <NewAcquisitionRequestPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/requests/:id"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view", "acquisitions.createRequest"]}>
            <DashboardPageWrapper>
              <AcquisitionRequestDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/receiving"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.receiveItems"]}>
            <DashboardPageWrapper>
              <ReceivingDashboardPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/reports"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <AcquisitionsReportsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Purchase Orders */}
      <Route
        path="/acquisitions/orders"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view", "acquisitions.createOrder"]}>
            <DashboardPageWrapper>
              <PurchaseOrdersPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/orders/new"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view", "acquisitions.createOrder"]}>
            <DashboardPageWrapper>
              <NewPurchaseOrderPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/orders/:id"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view", "acquisitions.createOrder"]}>
            <DashboardPageWrapper>
              <PurchaseOrderDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Acquisition Invoices */}
      <Route
        path="/acquisitions/invoices"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <AcqInvoicesPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/invoices/new"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <NewAcqInvoicePage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/acquisitions/invoices/:id"
        element={
          <ProtectedRoute requiredPermissions={["acquisitions.view"]}>
            <DashboardPageWrapper>
              <AcqInvoiceDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Patron Recommendation (any authenticated user) */}
      <Route
        path="/recommend"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <PatronRecommendPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-recommendations"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <MyRecommendationsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-recommendations/:id"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <MyRecommendationDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Catalog - List & View: require catalog.view staff & patron */}
      <Route
        path="/catalog"
        element={
          <ProtectedRoute requiredPermissions={["catalog.view", "catalog.list"]}>
            <DashboardPageWrapper>
              <CatalogManagementPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/physical/:id"
        element={
          <ProtectedRoute requiredPermissions={["catalog.view", "catalog.list", "book.view"]}>
            <DashboardPageWrapper>
              <BookDetailsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/journal/:id"
        element={
          <ProtectedRoute requiredPermissions={["catalog.view", "catalog.list", "book.view"]}>
            <DashboardPageWrapper>
              <JournalDetailsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/acquisition-cataloging"
        element={
          <ProtectedRoute requiredPermissions={["catalog.view", "catalog.list", "book.view"]}>
            <DashboardPageWrapper>
              <AcquisitionCatalogingWizardPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/digital/:id"
        element={
          <ProtectedRoute requiredPermissions={["catalog.view", "catalog.list", "digital.view"]}>
            <DashboardPageWrapper>
              <DigitalResourceDetailsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Catalog - Create: require catalog.create */}
      <Route
        path="/catalog/isbn"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "book.create"]}>
            <DashboardPageWrapper>
              <IsbnCatalogPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/manual"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "book.create"]}>
            <DashboardPageWrapper>
              <ManualCatalogPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/upload"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "digital.upload"]}>
            <DashboardPageWrapper>
              <UploadEResourcePage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/journal"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "book.create"]}>
            <DashboardPageWrapper>
              <JournalCatalogPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/journal/draft/:id"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "catalog.edit"]}>
            <DashboardPageWrapper>
              <JournalCatalogPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/bulk-import"
        element={
          <ProtectedRoute requiredPermissions={["catalog.bulkImport"]}>
            <DashboardPageWrapper>
              <BulkImportPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/bulk-export"
        element={
          <ProtectedRoute requiredPermissions={["catalog.bulkExport"]}>
            <DashboardPageWrapper>
              <BulkExportPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/catalog/my-stats"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "catalog.edit", "catalog.view"]}>
            <DashboardPageWrapper>
              <MyCatalogStatsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/catalog/drafts"
        element={
          <ProtectedRoute requiredPermissions={["catalog.create", "catalog.edit"]}>
            <DashboardPageWrapper>
              <CatalogDraftsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff/catalog/review-queue"
        element={
          <ProtectedRoute requiredPermissions={["catalog.review"]}>
            <DashboardPageWrapper>
              <CatalogReviewQueuePage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/cataloging"
        element={
          <ProtectedRoute requiredPermissions={["reports.cataloging"]}>
            <DashboardPageWrapper>
              <CatalogingProductivityReportPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Catalog - Edit: require catalog.edit */}
      <Route
        path="/catalog/physical/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["catalog.edit", "book.edit"]}>
            <DashboardPageWrapper>
              <ManualCatalogPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/digital/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["catalog.edit", "digital.edit"]}>
            <DashboardPageWrapper>
              <EditDigitalResourcePage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/paid-purchases"
        element={
          <ProtectedRoute requiredPermissions={["digital.paidAccess.manage", "digital.*"]}>
            <DashboardPageWrapper>
              <PaidResourcePurchasesPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/catalog/paid-entitlements"
        element={
          <ProtectedRoute requiredPermissions={["digital.paidAccess.manage", "digital.*"]}>
            <DashboardPageWrapper>
              <PaidResourceEntitlementsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Redirect old settings routes to setup */}
      <Route path="/settings" element={<Navigate to="/setup" replace />} />
      <Route path="/settings/*" element={<Navigate to="/setup" replace />} />
      <Route path="/admin/settings/domain" element={<Navigate to="/setup/domain" replace />} />
      <Route
        path="/tenants"
        element={
          <ProtectedRoute requireSuperAdmin>
            <DashboardPageWrapper>
              <TenantManagementPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing-plans"
        element={
          <ProtectedRoute requireSuperAdmin>
            <DashboardPageWrapper>
              <BillingPlansPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute requireTenantAdmin>
            <DashboardPageWrapper>
              <TenantBillingPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* Circulation */}
      <Route
        path="/circulation"
        element={
          <ProtectedRoute
            requiredPermissions={[
              "circulation.checkOut",
              "circulation.checkIn",
              "circulation.borrow",
              "circulation.return",
            ]}
          >
            <DashboardPageWrapper>
              <CirculationDeskPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/circulation/holds"
        element={
          <ProtectedRoute requiredPermissions={["circulation.manageHolds", "circulation.reserve"]}>
            <HoldsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/circulation/overdue"
        element={
          <ProtectedRoute requiredPermissions={["circulation.overdueManagement"]}>
            <OverduePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/circulation/history"
        element={
          <ProtectedRoute requiredPermissions={["circulation.viewHistory"]}>
            <HistoryPage />
          </ProtectedRoute>
        }
      />
      {/* Fines & Fees Management (Staff) */}
      <Route
        path="/fines"
        element={
          <ProtectedRoute requiredPermissions={["fines.view"]}>
            <FinesManagementPage />
          </ProtectedRoute>
        }
      />
      {/* Patron Self-Service */}
      <Route
        path="/my-books"
        element={
          <ProtectedRoute>
            <MyBooksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-holds"
        element={
          <ProtectedRoute>
            <MyHoldsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-fines"
        element={
          <ProtectedRoute>
            <MyFinesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/paid-resources"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <PaidResourcesPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/paid-resources/:id"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <PaidResourceDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* ── Bindery Management (staff) ─────────────────────── */}
      <Route
        path="/bindery"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view"]}>
            <DashboardPageWrapper>
              <BinderyDashboardPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/vendors"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view", "bindery.manageVendors"]}>
            <DashboardPageWrapper>
              <BinderyVendorsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/vendors/new"
        element={
          <ProtectedRoute requiredPermissions={["bindery.manageVendors"]}>
            <DashboardPageWrapper>
              <BinderyVendorFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/vendors/:id/edit"
        element={
          <ProtectedRoute requiredPermissions={["bindery.manageVendors"]}>
            <DashboardPageWrapper>
              <BinderyVendorFormPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/vendors/:id"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view"]}>
            <DashboardPageWrapper>
              <BinderyVendorDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/requests"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view", "bindery.createRequest"]}>
            <DashboardPageWrapper>
              <BinderyRequestsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/requests/new"
        element={
          <ProtectedRoute requiredPermissions={["bindery.createRequest"]}>
            <DashboardPageWrapper>
              <NewBinderyRequestPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/requests/:id"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view", "bindery.createRequest"]}>
            <DashboardPageWrapper>
              <BinderyRequestDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/history"
        element={
          <ProtectedRoute requiredPermissions={["bindery.view"]}>
            <DashboardPageWrapper>
              <BinderyHistoryPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* ── Thesis (staff) ─────────────────────────────────── */}
      <Route
        path="/bindery/thesis"
        element={
          <ProtectedRoute requiredPermissions={["thesis.view", "thesis.manage"]}>
            <DashboardPageWrapper>
              <ThesisRequestsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/thesis/settings/pricing"
        element={
          <ProtectedRoute requiredPermissions={["thesis.manage"]}>
            <DashboardPageWrapper>
              <ThesisPricingSettingsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bindery/thesis/:id"
        element={
          <ProtectedRoute requiredPermissions={["thesis.view", "thesis.manage"]}>
            <DashboardPageWrapper>
              <ThesisRequestDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* ── Thesis (patron) ────────────────────────────────── */}
      <Route
        path="/my/thesis"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <MyThesisRequestsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my/thesis/new"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <NewThesisRequestPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my/thesis/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <EditThesisRequestPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my/thesis/:id"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <MyThesisDetailPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      {/* ── Support / Help Desk ─────────────────────────────── */}
      <Route
        path="/support"
        element={
          <ProtectedRoute requiredPermissions={["support.viewOwn"]}>
            <DashboardPageWrapper>
              <MyTicketsPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/new"
        element={
          <ProtectedRoute requiredPermissions={["support.createTicket"]}>
            <DashboardPageWrapper>
              <NewTicketPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/:id"
        element={
          <ProtectedRoute requiredPermissions={["support.viewOwn"]}>
            <DashboardPageWrapper>
              <TicketDetailPage viewer="patron" />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute requiredPermissions={["support.view"]}>
            <DashboardPageWrapper>
              <SupportInboxPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support/:id"
        element={
          <ProtectedRoute requiredPermissions={["support.view"]}>
            <DashboardPageWrapper>
              <TicketDetailPage viewer="staff" />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route path="/notifications/manage" element={<Navigate to="/notifications" replace />} />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/documentation/*"
        element={
          <ProtectedRoute>
            <DashboardPageWrapper>
              <DocumentationPage />
            </DashboardPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="/internal/user-services-guide"
        element={
          <ProtectedRoute requiredPermissions={["patron.view"]}>
            <UserServicesGuidePage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

export default AppRoutes;
